/**
 * WebView Bridge 的 JS 侧统一封装。
 *
 * 来源：`qzapps/shared/snippets/flutter-webview-bridge/bridge.ts`（协议见同目录 PROTOCOL.md）。
 * 复制而来，按本工程需要增删；协议信封/错误码/事件**不要改**。
 *
 * - JS → 原生：`{ method, requestId, params }`
 * - 原生 → JS：`{ requestId, ok: true, data }` 或 `{ requestId, ok: false, error, message? }`
 * - 原生主动推送：`{ event, payload }`
 *
 * handler 名与原生容器必须一致，这里用协议默认值 `bridge`。
 * 新增方法见 PROTOCOL.md §8（三端同步 + bump bridgeVersion）。
 */

export type BridgePlatform = 'ios' | 'android' | 'web' | 'remote';

export type KnownBridgeErrorCode =
  | 'unknown_method'
  | 'invalid_params'
  | 'account_not_found'
  | 'login_required'
  | 'account_banned'
  | 'rate_limited'
  | 'risk_verification'
  | 'insufficient_credits'
  | 'cancelled'
  | 'network_error'
  /** 薯薯：该账号所在平台还没实现这项能力 */
  | 'unsupported'
  | 'unknown';

/** 业务层可透传服务端字符串码，所以允许任意 string。 */
export type BridgeErrorCode = KnownBridgeErrorCode | (string & {});

export class BridgeError extends Error {
  readonly code: BridgeErrorCode;
  constructor(code: BridgeErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'BridgeError';
    this.code = code;
  }
}

type BridgeResponse = {
  requestId?: string;
  ok: boolean;
  data?: unknown;
  error?: string;
  message?: string;
};
type BridgeEvent = { event: string; payload?: unknown };
type EventHandler = (payload: unknown) => void;

const REQUEST_TIMEOUT_MS = 30_000;
const HANDLER_NAME = 'bridge';

let requestCounter = 0;
const pending = new Map<
  string,
  { resolve: (value: unknown) => void; reject: (error: BridgeError) => void; timer: number }
>();
const listeners = new Map<string, Set<EventHandler>>();

/**
 * 远程桥（本地开发用）：浏览器里访问页面，但 bridge 调用打到运行中的 App。
 *
 * 只在 dev 构建生效，opt-in：
 *   - `?bridge=1`     → App 的 MCP 在 `http://<当前主机>:8790/bridge`（127.0.0.1:5173 访问 → 打到 127.0.0.1:8790）
 *   - `?bridge=<url>` → 指定端点（真机 / 换端口时用，如 `http://192.168.1.9:8790`）
 *   - `?token=<t>`    → App 设置里开了访问令牌时带上
 * 配置写进 sessionStorage，SPA 内跳转和刷新都保持。
 */
type RemoteBridge = { endpoint: string; token: string | null };

function readRemoteBridge(): RemoteBridge | null {
  if (typeof window === 'undefined' || !import.meta.env.DEV) return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('bridge');
  const token = params.get('token');
  const KEY = 'bridge.remote';
  if (raw) {
    const base = raw === '1' ? `http://${window.location.hostname}:8790` : raw.replace(/\/$/, '');
    const endpoint = base.endsWith('/bridge') ? base : `${base}/bridge`;
    const config: RemoteBridge = { endpoint, token };
    try {
      sessionStorage.setItem(KEY, JSON.stringify(config));
    } catch {
      /* ignore */
    }
    console.info('[bridge] 远程桥已开启 →', endpoint);
    return config;
  }
  try {
    const saved = sessionStorage.getItem(KEY);
    return saved ? (JSON.parse(saved) as RemoteBridge) : null;
  } catch {
    return null;
  }
}

const remoteBridge = readRemoteBridge();

function detectPlatform(): BridgePlatform {
  if (typeof window === 'undefined') return 'web';
  // mock（?mock=1）会装假桥，优先级最高；其次真机 WebView；再次远程桥
  if (window.webkit?.messageHandlers?.[HANDLER_NAME]) return 'ios';
  if (window.flutter_inappwebview?.callHandler) return 'android';
  if (remoteBridge) return 'remote';
  return 'web';
}

/** 远程桥：fetch 打到 App 的 /bridge，返回信封与 WebView 完全一致。 */
async function callRemote<T>(method: string, params: Record<string, unknown>, timeoutMs: number): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(remoteBridge!.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(remoteBridge!.token ? { Authorization: `Bearer ${remoteBridge!.token}` } : {}),
      },
      body: JSON.stringify({ method, params }),
      signal: controller.signal,
    });
    if (!response.ok && response.status !== 200) {
      throw new BridgeError('network_error', `App 未响应（HTTP ${response.status}）。确认 App 在前台运行、MCP 服务已开启。`);
    }
    const envelope = (await response.json()) as BridgeResponse;
    if (envelope.ok) return (envelope.data ?? null) as T;
    throw new BridgeError(envelope.error || 'unknown', envelope.message);
  } catch (error) {
    if (error instanceof BridgeError) throw error;
    if ((error as Error)?.name === 'AbortError') {
      throw new BridgeError('network_error', '连接 App 超时，确认与 App 在同一网络、MCP 服务已开启。');
    }
    throw new BridgeError('network_error', `连不上 App：${String(error)}`);
  } finally {
    window.clearTimeout(timer);
  }
}

function settle(requestId: string, response: BridgeResponse) {
  const entry = pending.get(requestId);
  if (!entry) return;
  pending.delete(requestId);
  window.clearTimeout(entry.timer);
  if (response.ok) {
    entry.resolve(response.data ?? null);
  } else {
    entry.reject(new BridgeError(response.error || 'unknown', response.message));
  }
}

function dispatchEvent(event: BridgeEvent) {
  listeners.get(event.event)?.forEach((handler) => {
    try {
      handler(event.payload);
    } catch (error) {
      console.error('[bridge] event handler failed', event.event, error);
    }
  });
}

function installGlobals() {
  if (typeof window === 'undefined') return;
  if (!window.__bridgeResolve) {
    window.__bridgeResolve = (raw: unknown) => {
      const response = (typeof raw === 'string' ? JSON.parse(raw) : raw) as BridgeResponse;
      if (response?.requestId) settle(response.requestId, response);
    };
  }
  if (!window.__bridgeEmit) {
    window.__bridgeEmit = (raw: unknown) => {
      const event = (typeof raw === 'string' ? JSON.parse(raw) : raw) as BridgeEvent;
      if (event?.event) dispatchEvent(event);
    };
  }
}

installGlobals();

type CallOptions = { timeoutMs?: number };

function call<T = unknown>(
  method: string,
  params: Record<string, unknown> = {},
  options: CallOptions = {},
): Promise<T> {
  const platform = detectPlatform();
  if (platform === 'web') {
    return Promise.reject(new BridgeError('unknown_method', 'not in app'));
  }
  if (platform === 'remote') {
    return callRemote<T>(method, params, options.timeoutMs ?? REQUEST_TIMEOUT_MS);
  }
  const requestId = `b-${Date.now()}-${requestCounter++}`;
  const message = { method, requestId, params };

  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      pending.delete(requestId);
      // 不要把方法名透给用户（会变成 `bridge timeout: data.overview` 之类的黑话）
      reject(new BridgeError('network_error', '请求超时，请检查网络后重试。'));
    }, options.timeoutMs ?? REQUEST_TIMEOUT_MS);
    pending.set(requestId, { resolve: resolve as (value: unknown) => void, reject, timer });

    try {
      if (platform === 'ios') {
        window.webkit!.messageHandlers![HANDLER_NAME]!.postMessage(message);
      } else {
        void window
          .flutter_inappwebview!.callHandler!(HANDLER_NAME, message)
          .then((response) => settle(requestId, response as BridgeResponse))
          .catch((error) => {
            pending.delete(requestId);
            window.clearTimeout(timer);
            reject(new BridgeError('unknown', String(error)));
          });
      }
    } catch (error) {
      pending.delete(requestId);
      window.clearTimeout(timer);
      reject(new BridgeError('unknown', String(error)));
    }
  });
}

function on(event: string, handler: EventHandler): () => void {
  let handlers = listeners.get(event);
  if (!handlers) {
    handlers = new Set();
    listeners.set(event, handlers);
  }
  handlers.add(handler);
  return () => {
    handlers!.delete(handler);
  };
}

export const bridge = {
  call,
  on,
  get platform(): BridgePlatform {
    return detectPlatform();
  },
  get isNative(): boolean {
    return detectPlatform() !== 'web';
  },
};

// ---------------------------------------------------------------------------
// 类型化封装
// ---------------------------------------------------------------------------

export type AppInfo = {
  platform: 'ios' | 'android';
  appVersion: string;
  buildNumber: string;
  bridgeVersion: number;
  locale: string;
  theme: 'light' | 'dark';
};

export type BridgeAccount = {
  id: string;
  nickname: string;
  avatar: string;
  /** 是否可写（登录有效）；**不含 cookie** */
  canWrite: boolean;
  /** 演示（审核）账号：只在本机跑，可当演示流程的目标账号 */
  isDemo?: boolean;
  /** 平台标识：bxy / bxk / bxs / shushu … */
  appId?: string;
  /** 薯薯：账号所属内容平台（xiaohongshu / douyin …）与显示名、平台号 */
  platform?: string;
  platformLabel?: string;
  handle?: string;
  /** 薯薯：该账号平台已实现的能力（`postsBackup` / `postsMigration` / `postsVisibility`）。
   *  页面按能力决定显示什么，不要按平台名写分支。 */
  capabilities?: string[];
  /** 薯薯：平台用户 id（小红书 user_id 等），在线工具翻「我的收藏」等列表用。since bridgeVersion 6 */
  uid?: string;
};

export const app = {
  info: () => call<AppInfo>('app.info'),
  /** 首屏内容已可见（数据到位或已展示错误）时调用；不调则原生 3s 后自行收起占位 */
  ready: () => call<null>('app.ready').catch(() => null),
  toast: (message: string, style: 'info' | 'success' | 'error' = 'info') =>
    call<null>('app.toast', { message, style }),
  haptic: (style: 'light' | 'medium' | 'success' | 'error' = 'light') =>
    call<null>('app.haptic', { style }),
  setTitle: (title: string) => call<null>('app.setTitle', { title }),
  close: () => call<null>('app.close'),
  openUrl: (url: string) => call<null>('app.openUrl', { url }),
  /**
   * 在原生导航里推一个新 Web 页（同站路径），返回键回到当前页。
   * 不在 App 内或旧版 App 没有该方法时返回 false，调用方自己页内跳转。
   */
  openPage: async (path: string, title = '') => {
    if (!bridge.isNative) return false;
    try {
      await call<null>('app.openPage', { path, title });
      return true;
    } catch (e) {
      if (e instanceof BridgeError && e.code === 'unknown_method') return false;
      throw e;
    }
  },
  /**
   * 原生确认框（破坏性操作按钮标红），确认返回 true。旧版 App 没有该方法时退回 `window.confirm`。
   */
  confirm: async (options: { title: string; message?: string; confirmLabel?: string; destructive?: boolean }) => {
    try {
      return (await call<{ confirmed: boolean }>('app.confirm', options)).confirmed;
    } catch (e) {
      if (e instanceof BridgeError && e.code === 'unknown_method') {
        return window.confirm([options.title, options.message].filter(Boolean).join('\n\n'));
      }
      throw e;
    }
  },
  readClipboard: () => call<{ text: string | null }>('app.readClipboard'),
  writeClipboard: (text: string) => call<null>('app.writeClipboard', { text }),
  /** 系统分享面板（文字 / 链接）。薯薯 since bridgeVersion 6 */
  share: (options: { text?: string; url?: string }) => call<null>('app.share', options, { timeoutMs: 10 * 60_000 }),
  /** 展开平台分享短链（xhslink），返回落地地址；其他地址原样返回。薯薯 since bridgeVersion 6 */
  resolveUrl: (url: string) => call<{ url: string }>('app.resolveUrl', { url }),
  log: (level: 'debug' | 'info' | 'warn' | 'error', message: string) =>
    call<null>('app.log', { level, message }),
  openNative: (page: string, params: Record<string, unknown> = {}) =>
    call<null>('app.openNative', { page, params }),
};

export const account = {
  list: () => call<BridgeAccount[]>('account.list'),
  get: (accountId: string) => call<BridgeAccount>('account.get', { accountId }),
  pick: (filter: 'authenticated' | 'all' = 'all') =>
    call<BridgeAccount>('account.pick', { filter }),
};

export const local = {
  /** 通用本地读（只读，原生强制按账号过滤）。 */
  query: <T = Record<string, unknown>>(
    accountId: string,
    entity: string,
    options: {
      filter?: string;
      sortBy?: string;
      ascending?: boolean;
      fields?: string[];
      offset?: number;
      limit?: number;
    } = {},
  ) => call<{ items: T[]; total: number }>('local.query', { accountId, entity, ...options }),
  /** 各类型已备份到本机的条数（posts/likes/collects/collectedVideos/following）。 */
  counts: (accountId: string) =>
    call<Record<string, number>>('local.counts', { accountId }),
};

export type XhsRequestOptions = {
  /** 默认 edith；creator = 创作者域 */
  host?: 'edith' | 'creator' | 'web';
  /** 默认 GET；POST 只放行少数只读接口（笔记详情 feed） */
  method?: 'GET' | 'POST';
  /** 相对路径，前缀白名单：`/api/sns/web/`、`/web_api/sns/`、`/api/galaxy/`；参数放 `query`，不要拼在 path 里 */
  path: string;
  query?: Record<string, string | number | boolean>;
  body?: Record<string, unknown>;
};

/**
 * 薯薯 · 小红书在线接口（百宝箱工具用）。Cookie / 签名 / 节流 / 错误分类都在原生，页面只传 accountId。
 * 与搬小书不同：`request` 返回的是上游的 **`data` 字段**，业务失败（success=false）原生直接抛
 * `login_required` / `rate_limited` / `risk_verification` / `unknown`。since bridgeVersion 6
 */
export const xhs = {
  request: <T = Record<string, unknown>>(accountId: string, options: XhsRequestOptions) =>
    call<{ data: T }>('xhs.request', { accountId, ...options }),
  unlikeNote: (accountId: string, noteId: string) => call<{ noteId: string }>('xhs.unlikeNote', { accountId, noteId }),
  uncollectNote: (accountId: string, noteId: string) =>
    call<{ noteId: string }>('xhs.uncollectNote', { accountId, noteId }),
  unfollowUser: (accountId: string, userId: string) =>
    call<{ userId: string }>('xhs.unfollowUser', { accountId, userId }),
};

/** 评论备份进度（`comments.progress` 事件 / `comments.state`）。 */
export type CommentBackupSnapshot = {
  state: 'idle' | 'running' | 'paused' | 'completed' | 'cancelled' | 'failed';
  taskId?: string;
  /** 要处理的篇数 / 已完成 / 失败 */
  total?: number;
  done?: number;
  failed?: number;
  /** 本次已存下的评论条数 */
  comments?: number;
  /** 正在处理的笔记标题 */
  current?: string;
  error?: string;
  /** `comments.state` 才带：可备份篇数、本机已有评论备份的篇数 / 条数 */
  stats?: { targets: number; notes: number; comments: number };
};

export type BackedUpComment = {
  id: string;
  author: string;
  text: string;
  replyCount: number;
  likeCount: number | null;
  /** 毫秒 */
  createdAt: number | null;
  fetchedAt: number;
};

/**
 * 薯薯 · 评论备份：对本机已备份的笔记逐篇读全一级评论存到本机（原生任务引擎，可暂停续跑、任务中心可见）。
 * 暂停 / 继续用 `task.pause / resume`。since bridgeVersion 6
 */
export const comments = {
  start: (accountId: string) => call<CommentBackupSnapshot>('comments.start', { accountId }),
  state: (accountId: string) => call<CommentBackupSnapshot>('comments.state', { accountId }),
  list: (accountId: string, id: string) => call<{ items: BackedUpComment[] }>('comments.list', { accountId, id }),
  onProgress: (handler: (snapshot: CommentBackupSnapshot & { accountId?: string }) => void) =>
    on('comments.progress', (payload) => handler(payload as CommentBackupSnapshot & { accountId?: string })),
};

/** 薯薯 · 导出笔记：本机已备份的笔记打 zip 后弹系统分享。since bridgeVersion 6 */
export const exportApi = {
  summary: (accountId: string) => call<{ notes: number; bytes: number }>('export.summary', { accountId }),
  posts: (accountId: string) =>
    call<{ notes: number; bytes: number }>('export.posts', { accountId }, { timeoutMs: 15 * 60_000 }),
};

/** 保存图片 / 视频到系统相册（地址限平台 CDN）。since bridgeVersion 6 */
export const media = {
  save: (items: { url: string; type: 'image' | 'video' }[]) =>
    call<{ saved: number; failed: number }>('media.save', { items }, { timeoutMs: 5 * 60_000 }),
};

/** 通用后端请求：签名 / device_id 由原生加，密钥不过桥。路径有前缀白名单。 */
export const host = {
  request: <T = Record<string, unknown>>(path: string, payload?: Record<string, unknown>) =>
    call<T>('host.request', payload ? { path, payload } : { path }),
};

export type TaskCounts = {
  total: number;
  migrated: number;
  failed: number;
  pending: number;
};

/** 单个备份类型的状态（`BackupSnapshot.types[]`）。 */
export type BackupTypeState = {
  /** 与页面 TYPES[].key 对齐（posts/likes/collects/collectedVideos/following） */
  type: string;
  status: string;
  processed: number;
};

/** 备份任务快照（`backup.status` / `backup.progress` 事件）。 */
export type BackupSnapshot = {
  accountId?: string;
  taskId?: string;
  state: 'idle' | 'running' | 'paused' | 'completed' | 'failed';
  progress?: number;
  processed?: number;
  /** 当前正在备份的类型（web key） */
  type?: string;
  /** 本次运行的逐类型状态 */
  types?: BackupTypeState[];
  error?: string;
};

/** 迁移任务快照（`migration.state` / `migration.progress` 事件）。 */
export type MigrationSnapshot = {
  accountId?: string;
  taskId?: string;
  /** preparing = 迁移前的增量补拉阶段 */
  state:
    | 'idle'
    | 'blocked'
    | 'preparing'
    | 'pending'
    | 'running'
    | 'paused'
    | 'completed'
    | 'failed'
    | 'cancelled';
  counts?: TaskCounts;
  selectedTypes?: string[];
  /** 「点赞转收藏」：点赞条目按收藏执行（迁移内容仍记为 likes） */
  likesAsCollects?: boolean;
  /** 当前正在迁移的条目（“类型：标题”） */
  current?: string;
  /** 补拉阶段的进度（state=preparing 时）：正在补拉的类型 / 已处理条数 / 当前条目 */
  prefetch?: { type: string; processed: number; item?: string };
  /** 补拉相关说明（跳过补拉 / 源账号离线 / 补拉未完成） */
  note?: string;
  error?: string;
};

/** 薯薯：某账号可备份 / 可迁移的数据类型。随平台变化（小红书、抖音不同），由原生下发。 */
export type DataTypeOption = {
  key: string;
  label: string;
  /** 本机已备份条数（迁移页用作源数据量） */
  count?: number;
};

/** 薯薯：`backup.types` / `migration.types` 的返回。`supported=false` 时页面只展示、不能开跑。 */
export type DataTypeCatalog = {
  supported: boolean;
  /** 不支持时给用户看的说明（如「即将支持」） */
  note?: string;
  types: DataTypeOption[];
};

export const backup = {
  /** 薯薯：该账号可备份的类型与本机已备份条数 */
  types: (accountId: string) => call<DataTypeCatalog>('backup.types', { accountId }),
  start: (accountId: string, types: string[]) =>
    call<BackupSnapshot>('backup.start', { accountId, types }),
  status: (accountId: string) => call<BackupSnapshot>('backup.status', { accountId }),
  pause: (accountId: string) => call<BackupSnapshot>('backup.pause', { accountId }),
  resume: (accountId: string) => call<BackupSnapshot>('backup.resume', { accountId }),
};

/** 源账号可迁移数据的条数（本地优先，缺失用实时 stats 兜底）。 */
export type MigrationSourceStats = {
  accountId: string;
  /** 该账号是否备份过（任一类型本地 > 0） */
  hasLocalBackup: boolean;
  /** 各迁移类型条数：posts / likes / following / collects / collectFolders */
  counts: Record<string, number>;
  /** 实时接口拿到的值（可能缺项；缺 = 拿不到） */
  online?: Record<string, number | null>;
};

/** 单个迁移类型的筛选：条数上限 + 时间范围（按作品发布时间）。 */
export type MigrationFilter = {
  /** 最多迁移多少条；省略/<=0 = 不限制。 */
  maxCount?: number;
  /** 时间范围；all 省略。仅对作品/点赞/收藏生效。 */
  timeRange?: 'all' | 'sixMonths' | 'oneYear' | 'twoYears';
};

export const migration = {
  /** 薯薯：以该账号为源时可迁移的类型 */
  types: (accountId: string) => call<DataTypeCatalog>('migration.types', { accountId }),
  /** `skipPrefetch` = 跳过迁移前的增量补拉，直接用本地已备份数据。
   *  `filters` = 逐类型筛选（条数上限 / 时间范围），key 为类型名。
   *  `force` = 跳过开跑前的「目标账号可写」探针门禁。
   *  `likesAsCollects` = 「点赞转收藏」：点赞条目按收藏写入目标账号。 */
  start: (
    accountId: string,
    targetAccountId: string,
    types: string[],
    skipPrefetch = false,
    filters?: Record<string, MigrationFilter>,
    force = false,
    likesAsCollects = false,
  ) =>
    call<MigrationSnapshot>('migration.start', {
      accountId,
      targetAccountId,
      types,
      skipPrefetch,
      filters,
      force,
      likesAsCollects,
    }),
  state: (accountId: string) => call<MigrationSnapshot>('migration.state', { accountId }),
  pause: (accountId: string) => call<MigrationSnapshot>('migration.pause', { accountId }),
  resume: (accountId: string) => call<MigrationSnapshot>('migration.resume', { accountId }),
  sourceStats: (accountId: string) =>
    call<MigrationSourceStats>('migration.sourceStats', { accountId }),
};

export type NoteItem = {
  awemeId: string;
  desc: string;
  cover: string;
  createdAt: string;
  /**
   * 是否「仅自己可见」。原生给的是上次同步的缓存，字段缺省表示还没同步过。
   */
  isPrivate?: boolean;
  /** 播放/点赞/评论/分享/收藏，来自同一次 work_list 同步 */
  statistics?: WorkStatistics;
  /** 深度指标：完播率、2s 跳出率、平均播放时长、主页访问…（比率是 0~1 小数） */
  metrics?: Record<string, number | null>;
};

export type NotesListResult = {
  total: number;
  offset: number;
  /** 可见性快照时间（ISO），null/缺省表示还没同步过 */
  visibilityUpdatedAt?: string | null;
  items: NoteItem[];
};

/** `notes.refreshVisibility` 的返回：这次同步到的作品数与已隐藏数。 */
export type NotesVisibilityResult = {
  total: number;
  privateCount: number;
  visibilityUpdatedAt: string;
};

/** 批量设私密的进度快照（`state`: idle → running → completed | cancelled | failed）。 */
export type NotesSnapshot = {
  state: 'idle' | 'running' | 'paused' | 'completed' | 'cancelled' | 'failed';
  /** 薯薯：本次操作（`NoteActionKey`）与任务 id */
  action?: NoteActionKey;
  taskId?: string;
  total?: number;
  done?: number;
  failed?: number;
  /** 最近一条正在处理的 awemeId */
  current?: string;
  /** 本批次已成功设为「仅自己可见」的 awemeId（累计），页面据此就地更新状态 */
  doneIds?: string[];
  /** 薯薯：`all` = 整个账号批量（边翻线上列表边执行），`selected` = 勾选的笔记 */
  scope?: 'all' | 'selected';
  /** 薯薯整账号批量：已是目标状态、没动的篇数 */
  skipped?: number;
  /** 薯薯：正常结束但有说明（如清空评论时剩几条反复删不掉、到单次上限） */
  note?: string;
  error?: string;
};

export const notes = {
  /** 本机备份的笔记（按发布时间倒序，offset/limit 默认最多 500 条）。 */
  list: (accountId: string, options: { offset?: number; limit?: number } = {}) =>
    call<NotesListResult>('notes.list', { accountId, ...options }),
  /** 向抖音拉一次全量作品状态，更新「仅自己可见」缓存并返回统计。 */
  refreshVisibility: (accountId: string) =>
    call<NotesVisibilityResult>('notes.refreshVisibility', { accountId }),
  /** 批量把抖音上的笔记设为「仅自己可见」；进度走 `notes.progress` 事件。 */
  setPrivate: (accountId: string, awemeIds: string[]) =>
    call<NotesSnapshot>('notes.setPrivate', { accountId, awemeIds }),
  /** 带 accountId 取该账号最近一次笔记操作，不带取全局在跑的那个 */
  state: (accountId?: string) => call<NotesSnapshot>('notes.state', accountId ? { accountId } : {}),
  cancel: () => call<NotesSnapshot>('notes.cancel'),
  onProgress: (handler: (snapshot: NotesSnapshot) => void) =>
    on('notes.progress', (payload) => handler(payload as NotesSnapshot)),
  /** 薯薯：本机的笔记（平台无关，id 为 `content.id`）；`includeDeleted` 带上在薯薯里删除的。 */
  posts: (accountId: string, options: { offset?: number; limit?: number; includeDeleted?: boolean } = {}) =>
    call<ShushuPostsResult>('notes.posts', { accountId, ...options }),
  /** 薯薯：单篇（认出是哪一篇 + 这篇的操作记录）。 */
  get: (accountId: string, id: string) => call<NoteDetail>('notes.get', { accountId, id }),
  /**
   * 薯薯：对这些笔记能做的操作。**平台差异由原生算好**：只返回该账号支持的操作，
   * `applicable` 是对选中笔记里适用的条数（0 = 都不需要），`disabledReason` 有值时不可点。
   */
  actions: (accountId: string, ids: string[]) =>
    call<{ actions: NoteAction[] }>('notes.actions', { accountId, ids }),
  /** 薯薯：整个账号的操作目录（`applicable` 是本机估算，线上可能更多；破坏性操作带 `checklist`）。since v5 */
  allActions: (accountId: string) =>
    call<{ actions: NoteAction[] }>('notes.actions', { accountId, scope: 'all' }),
  /** 薯薯：对整个账号的线上笔记执行（原生边翻列表边处理）；进度走 `notes.progress`。since v5 */
  runAll: (accountId: string, action: NoteActionKey) =>
    call<NotesSnapshot>('notes.run', { accountId, action, scope: 'all' }),
  /** 薯薯：执行操作（只对适用的笔记入队）；进度走 `notes.progress`。 */
  run: (accountId: string, action: NoteActionKey, ids: string[]) =>
    call<NotesSnapshot>('notes.run', { accountId, action, ids }),
  /** 薯薯：批量设置可见性（`private=true` 仅自己可见，false 公开）；进度走 `notes.progress`。 */
  setVisibility: (accountId: string, ids: string[], isPrivate: boolean) =>
    call<NotesSnapshot>('notes.setVisibility', { accountId, ids, private: isPrivate }),
};

/** 薯薯 `notes.posts` 的一条。 */
export type ShushuPost = {
  id: string;
  title: string;
  cover: string;
  /** 毫秒；拿不到发布时间的平台为 null */
  publishedAt: number | null;
  visibility: 'public' | 'private' | 'friends' | 'reviewing' | null;
  /** 本机是否已备份 */
  backedUp: boolean;
  /** 在薯薯里删除了线上内容（本机备份保留） */
  deleted: boolean;
  metrics: Record<string, number>;
};

/** `clearComments`：清空单篇笔记的评论（只在单篇目录里出现，since v5） */
export type NoteActionKey = 'hide' | 'unhide' | 'delete' | 'clearComments' | (string & {});

/** 操作目录的一项（原生 `PostAction` 下发，Web 只渲染）。 */
export type NoteAction = {
  key: NoteActionKey;
  label: string;
  detail: string;
  /** 不可恢复：红色、单独分组、必须确认 */
  destructive: boolean;
  /** 对当前选中笔记适用的条数 */
  applicable: number;
  confirm: { title: string; message: string; confirmLabel: string };
  disabledReason?: string;
  /** 整账号目录：本机没有备份的篇数 */
  unbacked?: number;
  /** 整账号目录的破坏性操作：开始前要逐条勾选的确认项（全部勾选才能开始） */
  checklist?: string[];
};

export type NoteHistoryEntry = {
  taskId: string;
  name: string;
  status: 'pending' | 'done' | 'failed';
  /** 毫秒 */
  at: number;
  error?: string;
};

/** 薯薯 `notes.get`：认出是哪一篇 + 操作记录。 */
export type NoteDetail = Omit<ShushuPost, 'metrics'> & {
  type: string;
  /** 平台上的链接（可能打不开：已删除 / 仅自己可见） */
  url: string | null;
  history: NoteHistoryEntry[];
};

export type ShushuPostsResult = { total: number; offset: number; items: ShushuPost[] };

export type MetricTrend = {
  /** `20260919` 形式 */
  date: string;
  value: number | null;
  /** 环比变化率（原生只在部分日期给） */
  changeRate?: number | null;
};

export type DataMetric = {
  /** `play_cnt` / `digg_cnt` / … */
  key: string;
  name: string;
  value: number | null;
  trends: MetricTrend[];
};

export type DataOverview = {
  recentDays: number;
  metrics: DataMetric[];
};

export type WorkStatistics = {
  play: number | null;
  digg: number | null;
  comment: number | null;
  share: number | null;
  collect: number | null;
};

export type WorkItem = {
  /** 字符串 id，与本地库的 `PostVideo.awemeId` 一致 */
  awemeId: string;
  desc: string;
  cover: string;
  /** 毫秒时间戳 */
  createdAt: number | null;
  statistics: WorkStatistics;
  /**
   * 深度指标（完播率 / 2s 跳出率 / 封面点击率 / 平均播放时长 / 主页访问 / 粉丝增减…），
   * 来自创作者中心 `pc/work_list` 的 `items[].metrics`，比率是 0~1 的小数。
   * 与 Excel 导出的列等价 —— 有它就不需要再手动上传文件。
   */
  metrics?: Record<string, number | null>;
};

export type WorksResult = {
  items: WorkItem[];
  hasMore: boolean;
  total: number;
  /** 下一页游标 */
  cursor: number;
};

/** 创作者中心「数据中心」读接口（只需 cookie + csrf，由原生代签）。 */
export const data = {
  overview: (accountId: string, recentDays = 7) =>
    call<DataOverview>('data.overview', { accountId, recentDays }),
  works: (accountId: string, options: { count?: number; cursor?: number } = {}) =>
    call<WorksResult>('data.works', { accountId, ...options }),
};

export type TaskStatus =
  | 'idle'
  | 'pending'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'cancelled';

/** 任务中心统一快照（备份 / 迁移同一套字段）。 */
export type TaskSnapshot = {
  taskId: string;
  type: 'backup' | 'migration' | (string & {});
  name: string;
  status: TaskStatus;
  accountId?: string;
  accountNickname?: string;
  current?: number;
  total?: number;
  successCount?: number;
  failedCount?: number;
  /** 毫秒时间戳 */
  createdAt?: number;
  error?: string;
  pauseReason?: string;
};

/** 任务的逐条明细（迁移任务才有）。 */
export type TaskItem = {
  id: string;
  title: string;
  type: string;
  status: 'pending' | 'migrated' | 'failed';
  error?: string;
  migratedAt?: number;
};

export type TaskDetail = {
  task: TaskSnapshot & { startedAt?: number; completedAt?: number };
  summary: { total?: number; migrated?: number; failed?: number; pending?: number };
  /** 备份任务不逐条落库，为 false 时没有明细可看 */
  hasItems: boolean;
};

export const task = {
  list: () => call<{ tasks: TaskSnapshot[] }>('task.list'),
  get: (taskId: string) => call<TaskDetail>('task.get', { taskId }),
  items: (taskId: string, options: { failedOnly?: boolean; limit?: number } = {}) =>
    call<{ total: number; failedTotal: number; returned: number; items: TaskItem[] }>(
      'task.items',
      { taskId, ...options },
    ),
  pause: (taskId: string) => call<{ tasks: TaskSnapshot[] }>('task.pause', { taskId }),
  resume: (taskId: string) => call<{ tasks: TaskSnapshot[] }>('task.resume', { taskId }),
  delete: (taskId: string) => call<{ tasks: TaskSnapshot[] }>('task.delete', { taskId }),
};

export const track = {
  event: (name: string, metadata: Record<string, unknown> = {}) =>
    call<null>('track.event', { name, metadata }).catch(() => null),
  /** 容器通常已按页面打过 page_open；只给页面内的子页面用，避免双计 */
  pageOpen: (page: string) => call<null>('track.pageOpen', { page }).catch(() => null),
};

declare global {
  interface Window {
    webkit?: {
      messageHandlers?: Record<string, { postMessage: (message: unknown) => void } | undefined>;
    };
    flutter_inappwebview?: {
      callHandler?: (name: string, ...args: unknown[]) => Promise<unknown>;
    };
    __bridgeResolve?: (raw: unknown) => void;
    __bridgeEmit?: (raw: unknown) => void;
  }
}

// ─── 薯薯全能通 · 数据分析（analytics.*，since shushu bridge v1） ───────────────
//
// 指标怎么显示（格式、越低越好）随数据下发，页面只管排版；
// 接新平台 / 新账号类型时原生多给几个指标，页面不用改。

/** count: 1.2万 · signedCount: +23 · percent: 0~1 → 17.3% · seconds: 4.1s */
export type MetricFormat = 'count' | 'signedCount' | 'percent' | 'seconds';

export type AnalyticsMetric = {
  key: string;
  title: string;
  format: MetricFormat;
  /** sum 周期求和 · average 周期平均 · latest 周期末快照 */
  aggregation: 'sum' | 'average' | 'latest';
  /** 越小越好（如 2 秒跳出率），环比颜色反过来 */
  lowerIsBetter: boolean;
  value: number | null;
  /** 上一周期的值，用来算环比 */
  previous: number | null;
  /** 当前周期每日值，`date` 为 yyyy-MM-dd */
  trend: { date: string; value: number }[];
};

export type AnalyticsOverview = {
  accountId: string;
  platform: string;
  recentDays: number;
  /** 最近一次同步（毫秒），没同步过为 null */
  updatedAt: number | null;
  metrics: AnalyticsMetric[];
};

export type AnalyticsWork = {
  id: string;
  title: string;
  cover: string;
  url: string;
  type: string;
  /** 毫秒时间戳；拿不到发布时间的平台为 null */
  publishedAt: number | null;
  /** views / likes / comments / collects / shares / completion_rate / bounce_rate_2s / homepage_visits … */
  metrics: Record<string, number>;
};

export const analytics = {
  overview: (accountId: string, recentDays: 7 | 30 = 7) =>
    call<AnalyticsOverview>('analytics.overview', { accountId, recentDays }),
  works: (accountId: string, limit = 50) =>
    call<{ items: AnalyticsWork[] }>('analytics.works', { accountId, limit }),
};
